import React, { useMemo } from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import type { PriceList } from "shared/types/__generated__/graphql"
import type { InputContextType } from "shared/ui/input/Input"
import { Select } from "shared/ui/input/select/select/Select"

import { ProjectFormData } from "features/project-management/types/ProjectFormData"

import { useProjectFormContext } from "../contexts/ProjectFormContext"

const usePriceListOptions = (
  priceLists?: PriceList[] | null
): SelectOption<string>[] => {
  const priceListOptions: SelectOption<string>[] = useMemo(
    () =>
      priceLists?.map(list => ({
        value: list.id,
        label: list.title || ""
      })) || [],
    [priceLists]
  )

  return priceListOptions
}

interface UsePriceListWarningParams {
  priceListID?: string | null
  priceLists?: PriceList[] | null
}

const usePriceListWarning = ({
  priceListID,
  priceLists
}: UsePriceListWarningParams): string | null | undefined => {
  const { t } = useTranslation()

  const warningMessage = useMemo(() => {
    const currentPriceList = priceLists?.find(pl => pl.isCurrent)
    if (!currentPriceList?.title) return null
    return priceListID !== currentPriceList?.id
      ? t("Validation.NonStandardPriceListWarning", {
          currentPriceList: currentPriceList.title
        })
      : undefined
  }, [priceLists, priceListID, t])

  return warningMessage
}

interface ProjectFormPriceListReadonlyProps extends WithClassName {
  priceListID?: string | null
  priceLists?: PriceList[] | null
}

const ProjectFormPriceListReadonly = React.memo(
  ({
    className,
    priceListID,
    priceLists
  }: ProjectFormPriceListReadonlyProps) => {
    const { t } = useTranslation()

    const priceListOptions = usePriceListOptions(priceLists)
    const warningMessage = usePriceListWarning({ priceListID, priceLists })

    return (
      <Select.Root
        label={t("Price.PriceList")}
        className={className}
        data-testid="project__price-list"
        hasWarning={!!warningMessage}
        readonly
      >
        <Select.LabelContainer>
          <Select.Label />
          <Select.InfoTip
            content={warningMessage}
            variant="warning"
            label={t("Form.ShowInfoFor", { field: t("Price.PriceList") })}
            className="input__info-tip--warning"
          />
        </Select.LabelContainer>
        <Select.Readonly options={priceListOptions} value={priceListID} />
      </Select.Root>
    )
  }
)

interface ProjectFormPriceListEditableProps
  extends WithClassName,
    Partial<Omit<InputContextType, "name" | "label">> {
  priceLists?: PriceList[] | null
}

const ProjectFormPriceListEditable = React.memo(
  ({ className, priceLists, ...rest }: ProjectFormPriceListEditableProps) => {
    const { t } = useTranslation()
    const priceList = useWatch<ProjectFormData, "project.priceList">({
      name: "project.priceList"
    })
    const { isReadonly } = useProjectFormContext()

    const priceListOptions = usePriceListOptions(priceLists)
    const warningMessage = usePriceListWarning({
      priceListID: priceList,
      priceLists
    })

    return (
      <Select.Root
        name="project.priceList"
        label={t("Price.PriceList")}
        className={className}
        data-testid="project__price-list"
        readonly={isReadonly}
        hasWarning={!!warningMessage}
        {...rest}
      >
        <Select.LabelContainer>
          <Select.Label />
          <Select.InfoTip
            content={warningMessage}
            variant="warning"
            label={t("Form.ShowInfoFor", { field: t("Price.PriceList") })}
            className="input__info-tip--warning"
          />
        </Select.LabelContainer>

        <Select.Input options={priceListOptions} />
        <Select.Readonly options={priceListOptions} />
        <Select.ErrorMessage />
      </Select.Root>
    )
  }
)

export interface ProjectFormPriceListProps
  extends WithClassName,
    Partial<Omit<InputContextType, "name" | "label">> {}

export const ProjectFormPriceList = React.memo(
  ({ className, ...rest }: ProjectFormPriceListProps) => {
    const { isLoading, isReadonly, model } = useProjectFormContext()
    const priceLists = model?.pricing.data.priceLists?.data
    const priceList = useWatch<ProjectFormData, "project.priceList">({
      name: "project.priceList"
    })

    if (isLoading) {
      return null
    }

    if (!isReadonly) {
      return (
        <ProjectFormPriceListEditable
          className={className}
          priceLists={priceLists}
          {...rest}
        />
      )
    }

    return (
      <ProjectFormPriceListReadonly
        className={className}
        priceListID={priceList}
        priceLists={priceLists}
      />
    )
  }
)

ProjectFormPriceList.displayName = "ProjectFormPriceList"
